/// <amd-module name="DistributedTaskUI/Logs/TaskListLogsControllerView" />
import * as React from "react";

import { ExpandedLogsTaskHeader } from "DistributedTaskUI/Logs/ExpandedLogsTaskHeader";
import { ILogsViewerSection, ExpandedLogsViewerComponent } from "DistributedTaskUI/Logs/ExpandedLogsViewerComponent";
import { ITaskLog, TaskState, ILineSelection } from "DistributedTaskUI/Logs/Logs.Types";
import { LogsViewUtility } from "DistributedTaskUI/Logs/LogsViewUtility";
import { TaskLogStepComponent } from "DistributedTaskUI/Logs/TaskLogStepComponent";
import * as Resources from "DistributedTaskUI/Logs/Resources";
import { FocusZone, FocusZoneDirection } from "OfficeFabric/FocusZone";
import { List } from "OfficeFabric/List";
import { findScrollableParent } from "VSSUI/Utilities/Dom";
import { css, KeyCode } from "VSSUI/Util";

import { IVssPageContext } from "VSS/Platform/Context";

import "VSS/LoaderPlugins/Css!DistributedTaskUI/Logs/TaskListLogsControllerView";

export interface ITaskListLogsControllerViewState {
    showPanel: boolean;
    followTail: boolean;
    expandedTaskContent: string;
    fetchLogInProgress?: boolean;
    expandedTaskRank: number;
    defaultLogLineNumber?: number;
    selection?: ILineSelection;
}

export interface ITaskSelectionOptions {
    task: ITaskLog;
    selection: ILineSelection;
}

export interface ITaskListLogsControllerViewProps {
    // List of the task to be shown on the right pane
    tasks: ITaskLog[];
    // Key for the Vertical tab item for which this controller view render
    verticalTabItemKey?: string;
    // Callback when full log viewer panel opened on right pane
    onPanelOpen?: (key: string) => void;
    // Callback when full log viewer panel closed or changed on right pane
    onPanelChanged?: () => void;
    // Initial task index to scroll to when the component is mounted
    indexToScrollOnMount?: number;
    // Initial task options for which the log panel should be automatically opened on component mount
    selectTaskOptions?: ITaskSelectionOptions;
    // Page context
    pageContext?: IVssPageContext;
    // Optional custom renderer for the task log rendering
    onRenderTaskLog?: (props?: ITaskLog, taskIndex?: number, defaultRender?: (props?: ITaskLog, taskIndex?: number) => JSX.Element | null) => JSX.Element | null;
    // This is to control if auto scroll happen to logs view on right pane
    // By default it is set to false
    enableAutoScroll?: boolean;
    /**
     * Optional. Hides timestamps in logs view.
     */
    hideTimestamp?: boolean;

    /**
     * Only used by new log reader. Call back that gets triggered when the line has been scroll to view successfully, when 'lineToScroll' prop is set
     */
    onScrollToLine?: (lineNumber: number) => void;

    // task definition id to help url mapping
    taskDefinitionIdToHelpUrlMap?: { [id: string]: string };
}

const LogsViewUtility2 = LogsViewUtility; // This is for decreasing size of the minified script
const taskLogStepComponentContainerClassName = "task-log-step-component-container";

/**
 * Encapsulates the controller view for Logs corresponding to list of tasks
 */
export class TaskListLogsControllerView extends React.Component<ITaskListLogsControllerViewProps, ITaskListLogsControllerViewState> {
    public componentWillMount() {
        this._setInitialState();
        LogsViewUtility2.fixTasksRank(this.props.tasks);
    }

    public componentDidMount() {
        this._parentElement = this._getScrollParent(this._contentContainer);
        this._attachScrollEvent();
        this._isMounted = true;

        setTimeout(() => {
            // We need to add delay here since office fabric list render items in async fashion
            this._scrollRequiredItemToView();
            this._select();
        }, 0);
    }

    public componentDidUpdate(prevProps: ITaskListLogsControllerViewProps, prevState: ITaskListLogsControllerViewState) {
        if (!this._isAutoScrollDisabled) {
            this._scrollRequiredItemToView();
            if (this.props.selectTaskOptions !== prevProps.selectTaskOptions) {
                this._select();
            }
        }
    }

    public componentWillUnmount() {
        this._isMounted = false;
        this._detachScrollEvent();
    }

    public componentWillUpdate(nextProps: ITaskListLogsControllerViewProps, nextState: ITaskListLogsControllerViewState) {
        if (nextProps.tasks && nextProps.tasks.length > 0) {
            LogsViewUtility2.fixTasksRank(nextProps.tasks);
        }
    }

    public render(): React.ReactNode {
        if (this.props.tasks && this.props.tasks.length > 0) {
            return this._getTaskListLogsContentInternal();
        } else {
            return null;
        }
    }

    private _getTaskListLogsContentInternal(): JSX.Element {
        const expandedTask = this._getExpandedTask();
        const isLiveLog = this._isLiveLog(expandedTask);
        return (
            <div className="logs-task-list-container" ref={(element) => this._contentContainer = element}>
                <FocusZone
                    className="task-list-logs"
                    direction={FocusZoneDirection.vertical}
                    isInnerZoneKeystroke={(keyEvent: React.KeyboardEvent<HTMLElement>) => (
                        (keyEvent.which === KeyCode.rightArrow))}>
                    <List
                        role="list"
                        items={[...this.props.tasks]}
                        onShouldVirtualize={() => { return false; }}
                        onRenderCell={this._onRenderItem}>
                    </List>
                </FocusZone>
                {
                    this.state && this.state.showPanel &&
                    <ExpandedLogsViewerComponent
                        componentRef={(element) => this._logsViewerElement = element}
                        showPanel={this.state.showPanel}
                        fetchLogInProgress={this.state.fetchLogInProgress}
                        followTail={this.state.followTail}
                        isLive={isLiveLog}
                        showLineNumber={!isLiveLog} // Don't show line number when live log coming
                        content={this._getLogContent()}
                        onCloseClick={this._onCloseClick}
                        onRenderHeader={this._getHeader}
                        defaultLineNumber={this.state.defaultLogLineNumber}
                        pageContext={this.props.pageContext}
                        {...(expandedTask && expandedTask.linkingOptions) ? {
                            linkingOptions: expandedTask.linkingOptions
                        } : {}}
                        selection={this.state.selection}
                        hideTimestamp={this.props.hideTimestamp}
                        onScrollToLine={this.props.onScrollToLine}
                    />
                }
            </div >
        );
    }

    private _onRenderItem = (task: ITaskLog, taskIndex?: number) => {
        if (this.props.onRenderTaskLog) {
            const logComponent = this.props.onRenderTaskLog(task, taskIndex, this._getTaskLogComponent);
            if (logComponent) {
                return logComponent;
            }
        }

        return this._getTaskLogComponent(task, taskIndex);
    }

    private _getTaskLogComponent = (task: ITaskLog, taskIndex?: number): JSX.Element => {
        const inProgressClassName: string = (task && task.state === TaskState.InProgress && task.liveLogsData) ? "task-state-inprogress" : "";
        const taskHelpUrl = !!this.props.taskDefinitionIdToHelpUrlMap && !!task.taskDefinition ? this.props.taskDefinitionIdToHelpUrlMap![task.taskDefinition.id] : undefined;

        return (
            <TaskLogStepComponent
                key={task.timelineRecordId || task.taskId}
                task={task}
                showExpandedLogsView={this._showLogLineExpandedView}
                pageContext={this.props.pageContext}
                cssClass={css(taskLogStepComponentContainerClassName, inProgressClassName, `${taskLogStepComponentContainerClassName}-${(taskIndex || 0) + 1}`)}
                taskHelpUrl={taskHelpUrl} />
        );
    }

    private _getHeader = (): JSX.Element | null => {
        const expandedTask = this._getExpandedTask();
        const isLiveLog = this._isLiveLog(expandedTask);

        if (expandedTask) {
            return (
                <ExpandedLogsTaskHeader
                    task={expandedTask}
                    logUrl={expandedTask.logUrl}
                    isLive={isLiveLog}
                    onCloseClick={this._onCloseClick}
                    nextTaskButtonEnabled={!!LogsViewUtility2.getNextTask(this.props.tasks, this.state.expandedTaskRank)}
                    previousTaskButtonEnabled={!!LogsViewUtility2.getPreviousTask(this.props.tasks, this.state.expandedTaskRank)}
                    onNextTaskButtonClicked={this._onNextTaskClicked}
                    onPreviousTaskButtonClicked={this._onPreviousTaskClicked}
                    onFollowTailChange={this._onFollowTailChange}
                    onHeadingTextKeyDown={this._onHeadingTextKeyDown}
                    pageContext={this.props.pageContext}
                />
            );
        }

        return null;
    }

    private _onHeadingTextKeyDown = (evt: React.KeyboardEvent<HTMLElement>) => {
        if (this._logsViewerElement) {
            if (evt && evt.shiftKey && evt.keyCode === KeyCode.tab) {
                this._logsViewerElement.focus();
            }
        }
    }

    private _attachScrollEvent() {
        if (this._parentElement) {
            this._parentElement.addEventListener("scroll", this._disableAutoScroll);
        }
    }

    private _detachScrollEvent() {
        if (this._parentElement) {
            this._parentElement.removeEventListener("scroll", this._disableAutoScroll);
        }

        this._isAutoScrollDisabled = false;
    }

    private _disableAutoScroll = () => {
        const didUserScroll = !this._isAutoScrollInProgress;

        if (didUserScroll) {
            this._isAutoScrollDisabled = true;
        }
    }

    private _getScrollParent(element: HTMLElement | null): HTMLElement | null {
        return element && findScrollableParent(element);
    }

    private _showLogLineExpandedView = (task: ITaskLog, lineNumber?: number) => {
        this._showLogExpandedView(task, lineNumber ? {
            lineStart: lineNumber
        } as ILineSelection : undefined);
    }

    private _showLogExpandedView = (task: ITaskLog, selection?: ILineSelection) => {
        const lineNumber = selection ? selection.lineStart : undefined;
        let state = { showPanel: true, defaultLogLineNumber: lineNumber } as ITaskListLogsControllerViewState;
        if (!LogsViewUtility2.isLiveComponentVisible(task)) {
            const currentTask = this._getExpandedTask();
            // set in progress loader only if tasks are different or if there's no currently expanded task
            if (!currentTask || task.taskId !== currentTask.taskId) {
                state.fetchLogInProgress = true;
            }
            LogsViewUtility2.fetchLogs(task).then((logs) => {
                this.setState({ expandedTaskContent: logs, fetchLogInProgress: false, defaultLogLineNumber: lineNumber, selection: selection } as ITaskListLogsControllerViewState);
                return;
            });
        } else {
            const latestLogsPromise = task.logUrl ? LogsViewUtility2.fetchLogs(task, true) : Promise.resolve("") as Promise<string>;
            latestLogsPromise.then((latestLogs: string) => {
                const content = latestLogs ? latestLogs : LogsViewUtility2.getLogsFromLiveLogData(task.liveLogsData);
                this.setState(
                    {
                        expandedTaskContent: content,
                        fetchLogInProgress: false,
                        defaultLogLineNumber: lineNumber,
                        followTail: true,
                        selection: selection
                    } as ITaskListLogsControllerViewState);
            });
        }

        const taskRank: number = task.rank;

        state.expandedTaskRank = taskRank;
        this.setState(state);

        if (this.props.onPanelOpen && this.props.verticalTabItemKey) {
            this.props.onPanelOpen(this.props.verticalTabItemKey);
        }
    }

    private _isLiveLog(expandedTask: ITaskLog | null): boolean {
        if (expandedTask) {
            return LogsViewUtility2.isLiveComponentVisible(expandedTask);
        }

        return false;
    }

    private _getExpandedTask(): ITaskLog | null {
        if (this.state) {
            const taskIndex = LogsViewUtility2.getTaskIndexForGivenRank(this.state.expandedTaskRank, this.props.tasks);
            if (taskIndex >= 0) {
                return this.props.tasks[taskIndex];
            }
        }
        return null;
    }

    private _onPreviousTaskClicked = () => {
        this._onPanelChanged();
        if (this.state) {
            const prevTask = LogsViewUtility2.getPreviousTask(this.props.tasks, this.state.expandedTaskRank);
            if (prevTask) {
                this._showLogExpandedView(prevTask);
            }
        }
    }

    private _onNextTaskClicked = () => {
        this._onPanelChanged();
        if (this.state) {
            const nextTask = LogsViewUtility2.getNextTask(this.props.tasks, this.state.expandedTaskRank);
            if (nextTask) {
                this._showLogExpandedView(nextTask);
            }
        }
    }

    private _onPanelChanged() {
        if (this.props.onPanelChanged) {
            this.props.onPanelChanged();
        }
    }

    private _getLogContent(): string | undefined {
        const expandedTask = this._getExpandedTask();

        if (this.state) {
            let content = (expandedTask && expandedTask.logUrl) ? this.state.expandedTaskContent : "";
            if (this.state.expandedTaskRank >= 0 && expandedTask && this._isLiveLog(expandedTask)) {
                const newContent = LogsViewUtility2.getLogsFromLiveLogData(expandedTask.liveLogsData);
                if (content !== newContent) {
                    content += newContent;
                }

                return content;
            } else {
                if (expandedTask && LogsViewUtility.hasTaskCompleted(expandedTask) && expandedTask.logUrl && !this._isPromiseCalled) {
                    let expandedViewerLogContent: string = "";
                    this._isPromiseCalled = true;
                    if (this._isMounted) {
                        this.setState({ fetchLogInProgress: true } as ITaskListLogsControllerViewState);
                    }
                    LogsViewUtility2.fetchLogs(expandedTask).then((logs) => {
                        expandedViewerLogContent = logs;
                    })
                        .catch((error) => {
                            expandedViewerLogContent = Resources.ExpandedLogDownloadFailureMessage;
                        })
                        .then(() => {
                            if (this._isMounted) {
                                this.setState({ expandedTaskContent: expandedViewerLogContent, fetchLogInProgress: false } as ITaskListLogsControllerViewState);
                            }
                        });
                }
                return this.state && this.state.expandedTaskContent;
            }
        }
    }

    private _onCloseClick = () => {
        this._setInitialState();
        this._onPanelChanged();
    }

    private _onFollowTailChange = (value: boolean) => {
        this.setState({ followTail: value } as ITaskListLogsControllerViewState);
    }

    private _setInitialState() {
        this.setState({
            showPanel: false,
            fetchLogInProgress: false,
            followTail: false,
            expandedTaskName: "",
            expandedTaskLogUrl: "",
            expandedTaskContent: "",
            expandedTaskRank: -1,
            defaultLogLineNumber: -1,
            selection: undefined
        } as ITaskListLogsControllerViewState);
    }

    private _scrollRequiredItemToView() {
        if (this.props.enableAutoScroll) {
            this._isAutoScrollInProgress = true;

            const itemIndex = this.props.indexToScrollOnMount as number;
            if (this._contentContainer) {
                const taskComponents = this._contentContainer.getElementsByClassName(taskLogStepComponentContainerClassName);
                if (taskComponents && taskComponents.length > itemIndex) {
                    const taskComponent = taskComponents[itemIndex] as HTMLElement;
                    if (taskComponent) {
                        window.requestAnimationFrame(() => {
                            taskComponent.scrollIntoView({ block: "nearest" });
                            // Reset with some delay, as scroll event propagation will take some time
                            setTimeout(() => {
                                this._isAutoScrollInProgress = false;
                            }, 400);
                        });
                    }
                }
            }
        }
    }

    private _select() {
        if (this.props.selectTaskOptions && this.props.selectTaskOptions.task) {
            this._showLogExpandedView(this.props.selectTaskOptions.task, this.props.selectTaskOptions.selection);
        }
    }

    private _contentContainer: HTMLElement | null;
    private _parentElement: HTMLElement | null;
    private _logsViewerElement: ILogsViewerSection | null;

    // This is used to detect whether auto scroll behaviour got disabled or not after user scroll it manually
    private _isAutoScrollDisabled: boolean = false;
    // When we land into logs page, its possible that we scroll to some particular task, for example to make error task in view
    // Scroll event raised even when programatically we do scroll, hence added below variable to distinguish if code is doing scroll or its user driven scroll
    // If _isAutoScrollInProgress is set to true then don't set _isAutoScrollDisabled to true as we are doing scroll programatically
    private _isAutoScrollInProgress: boolean = false;
    private _isPromiseCalled: boolean = false;
    private _isMounted: boolean = false;
}
